import { toast } from 'react-toastify';
import DeviceStateNotification from 'components/DeviceStateNotification';
import * as ConnectionState from 'lib/devices/wrappers/DeviceStates';
import {
  CONNECTED_NOTIFICATIONS_OPTIONS,
  DISCONNECTED_NOTIFICATIONS_OPTIONS,
  CONNECTION_WAY,
} from 'data/constants';
import { translations } from 'i18n';
import { updateDocumentTitle, displaySystemNotification } from 'utils/utils';
import { sendAnalytics } from '@feelrobotics/ftap-connector';
import { sendToAnalytics } from 'lib/googleAnalytics';

/**
 * Interface describing a device object structure.
 * Adjust fields to match your actual data.
 */
export interface DeviceInterface {
  name: string;
  id: string;
  supportProvisioning?: boolean;

  wrapper?: {
    getConnectionMode?: () => string; // Or a more specific type if known
  };
}

/**
 * Structure that describes the object reference
 * passed to onGattServerDisconnected.
 */
export interface ObjRefInterface {
  connectionState: string; // or an enum / union if available
  setConnectionState: (state: string) => void;
  device: DeviceInterface;
  connect: () => Promise<void>;
}

/**
 * Dictionary containing display names that differ from the device's original name.
 * Devices not in this list will return their original name as the display name.
 */
const deviceDisplayNames: Record<string, string> = {
  'Keon R2': 'KEON',
  'Keon R1': 'KEON',
  'SWK-S63E': 'Alex NEO 2',
  'SWK-S63E-A': 'Alex NEO 2',
  'SWK-SE63': 'Alex NEO 2',
  'S63E Alex NEO 2': 'Alex NEO 2',
  'SWK-SE63E': 'Alex NEO 2',
  'PowerBlow R1': 'PowerBlow',
};

/**
 * Returns the "display name" of a device based on the deviceDisplayNames dictionary.
 */
export const getDeviceDisplayName = (deviceName: string): string => {
  return deviceDisplayNames[deviceName] || deviceName;
};

/**
 * Returns the display name of the device at `index`, followed by a counter when the
 * list holds several devices sharing that name. Two toys of the same model advertise
 * the same name, so without the counter they would be impossible to tell apart.
 *
 * The counter is kept apart from the name by the "#" sign: a plain number would turn
 * the second KEON into "KEON 2", which is the name of the different KEON2 model.
 */
export const getDeviceDisplayLabel = (devices: Array<{ name?: string }>, index: number): string => {
  const device = devices?.[index];
  if (!device) {
    return '';
  }

  const displayName = getDeviceDisplayName(device.name ?? '');
  const hasSameName = (dev: { name?: string }) =>
    getDeviceDisplayName(dev.name ?? '') === displayName;
  if (devices.filter(hasSameName).length < 2) {
    return displayName;
  }

  return `${displayName} #${devices.slice(0, index + 1).filter(hasSameName).length}`;
};

/**
 * Handler for the "GATT server disconnected" event.
 * If the disconnection was not initiated manually (e.g., via a "Disconnect" button),
 * it attempts to reconnect, or displays a notification otherwise.
 */
export const onGattServerDisconnected = async (objRef: ObjRefInterface): Promise<void> => {
  // Slight delay to catch a possible manual "Disconnect" action by the user
  await new Promise((resolve) => setTimeout(resolve, 100));

  if (objRef.connectionState === ConnectionState.DISCONNECTED) {
    // Disconnection was manually triggered
    return;
  }

  // Otherwise (all other disconnection events)
  objRef.setConnectionState(ConnectionState.DISCONNECTED);
  console.log('all others way of disconnected event');

  // If the tab is inactive (document.visibilityState === 'hidden')
  if (document.visibilityState === 'hidden') {
    updateDocumentTitle(objRef, false);
    displaySystemNotification(objRef, false);
  }

  toast.dismiss();
  toast(
    <DeviceStateNotification device={objRef.device} state={ConnectionState.CONNECTING} />,
    // @ts-ignore
    DISCONNECTED_NOTIFICATIONS_OPTIONS,
  );

  // Due to a Chromium bug, after ~2.5 minutes of continuous reconnection attempts,
  // reloading the page may be necessary
  const refreshPageAfter2Minutes = setTimeout(() => {
    window.location.reload();
  }, 140 * 1000);

  objRef
    .connect()
    .then(() => {
      objRef.setConnectionState(ConnectionState.CONNECTED);

      if (document.visibilityState === 'hidden') {
        updateDocumentTitle(objRef, true);
        displaySystemNotification(objRef, true);
      }

      toast.dismiss();
      toast(
        <DeviceStateNotification device={objRef.device} state={ConnectionState.CONNECTED} />,
        // @ts-ignore
        CONNECTED_NOTIFICATIONS_OPTIONS,
      );
    })
    .catch((err) => {
      objRef.setConnectionState(ConnectionState.DISCONNECTED);

      if (document.visibilityState === 'hidden') {
        displaySystemNotification(objRef, false, translations('Notifications.ReconnectManually'));
      }

      toast.dismiss();
      toast(
        <DeviceStateNotification
          device={objRef.device.name}
          state={ConnectionState.DISCONNECTED}
        />,
        // @ts-ignore
        DISCONNECTED_NOTIFICATIONS_OPTIONS,
      );
    })
    .finally(() => {
      clearTimeout(refreshPageAfter2Minutes);
      sendToAnalytics('disconnect', objRef.device.name, objRef.device.id);
      sendAnalytics('disconnect', objRef.device.name, objRef.device.id);
    });
};

/**
 * Checks if a device supports a "provisioning" process.
 */
export const isDeviceSupportProvisioning = (device?: DeviceInterface): boolean => {
  return !!device?.supportProvisioning;
};

/**
 * Returns true if the device connects via Bluetooth (CONNECTION_WAY.BT).
 */
export const getDeviceConnectionMode = (device?: DeviceInterface): boolean => {
  return (
    !!device?.wrapper?.getConnectionMode && device.wrapper.getConnectionMode() === CONNECTION_WAY.BT
  );
};

export const isDeviceConnectedByWIFI = (device?: DeviceInterface): boolean => {
  return (
    !!device?.wrapper?.getConnectionMode &&
    device.wrapper.getConnectionMode() === CONNECTION_WAY.WIFI
  );
};

/**
 * Reads data from a DataView and returns an ASCII string
 * if all bytes are within the printable ASCII range (32..126).
 * Returns an empty string if at least one byte falls outside this range.
 */
export const dataViewToAsciiString = (data: DataView): string => {
  // First, check if all bytes are in the ASCII printable range 32..126
  for (let i = 0; i < data.byteLength; i++) {
    const ch = data.getUint8(i);
    if (ch < 32 || ch > 126) {
      return '';
    }
  }

  // If all characters are printable ASCII, collect and return the string
  let asciiResult = '';
  for (let i = 0; i < data.byteLength; i++) {
    asciiResult += String.fromCharCode(data.getUint8(i));
  }
  return asciiResult;
};
